import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }
interface P { x: number; y: number; vx: number; vy: number }

// Định luật Charles & PT trạng thái khí lí tưởng (Lớp 12 — Bài 9/10): pV/T = hằng số.
// Giữ p, tăng T → V tăng (Charles, V ∝ T). Chỉnh p, T → quan sát pittông & pV/T không đổi.
const GasLawSimulation: React.FC<Props> = ({
  title = 'Định luật chất khí — pV/T = hằng số',
  description = 'Khí trong xilanh có pittông. Tăng nhiệt độ T (giữ p) → thể tích V tăng (định luật Charles). Đại lượng pV/T luôn không đổi.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const ps = useRef<P[]>([])
  const [running, setRunning] = useState(true)
  const [T, setT] = useState(300)   // K
  const [p, setP] = useState(2)     // áp suất tương đối
  const firstRef = useRef(false)
  const W = 300, H = 320
  const cylX = 70, cylW = 160, cylBottom = H - 20, cylTopMax = 30

  // V ∝ T/p (chuẩn hoá). Chiều cao cột khí.
  const Vnorm = (T / 300) / p
  const gasH = Math.max(40, Math.min(cylBottom - cylTopMax, Vnorm * 150))
  const pistonY = cylBottom - gasH

  const build = useCallback(() => {
    ps.current = Array.from({ length: 40 }, () => {
      const a = Math.random() * Math.PI * 2, sp = 0.8 + (T / 300) * 1.2
      return { x: cylX + 10 + Math.random() * (cylW - 20), y: pistonY + 10 + Math.random() * (gasH - 20), vx: Math.cos(a) * sp, vy: Math.sin(a) * sp }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { build() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  // cập nhật tốc độ theo T
  useEffect(() => {
    const sp = 0.8 + (T / 300) * 1.2
    ps.current.forEach((q) => { const m = Math.hypot(q.vx, q.vy) || 1; q.vx = q.vx / m * sp; q.vy = q.vy / m * sp })
  }, [T])

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    ctx.clearRect(0, 0, W, H)
    // xilanh
    ctx.strokeStyle = '#94a3b8'; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(cylX, cylTopMax - 6); ctx.lineTo(cylX, cylBottom); ctx.lineTo(cylX + cylW, cylBottom); ctx.lineTo(cylX + cylW, cylTopMax - 6); ctx.stroke()
    // khí
    ctx.fillStyle = 'rgba(59,130,246,0.06)'; ctx.fillRect(cylX, pistonY, cylW, gasH)
    // phân tử
    for (const q of ps.current) {
      q.x += q.vx; q.y += q.vy
      if (q.x < cylX + 5) { q.x = cylX + 5; q.vx = Math.abs(q.vx) }
      if (q.x > cylX + cylW - 5) { q.x = cylX + cylW - 5; q.vx = -Math.abs(q.vx) }
      if (q.y < pistonY + 5) { q.y = pistonY + 5; q.vy = Math.abs(q.vy) }
      if (q.y > cylBottom - 5) { q.y = cylBottom - 5; q.vy = -Math.abs(q.vy) }
      ctx.beginPath(); ctx.arc(q.x, q.y, 3.5, 0, Math.PI * 2); ctx.fillStyle = '#3b82f6'; ctx.fill()
    }
    // pittông
    ctx.fillStyle = '#64748b'; ctx.fillRect(cylX - 4, pistonY - 12, cylW + 8, 12)
    // tải trọng (áp suất)
    ctx.fillStyle = '#475569'; ctx.font = 'bold 12px sans-serif'
    for (let i = 0; i < p; i++) ctx.fillRect(cylX + cylW / 2 - 18 + (i % 3) * 14, pistonY - 12 - 12 * (Math.floor(i / 3) + 1), 12, 10)
    raf.current = requestAnimationFrame(draw)
  }, [gasH, pistonY, p])

  useEffect(() => {
    if (running) { if (!firstRef.current) { firstRef.current = true; onComplete?.() } raf.current = requestAnimationFrame(draw) }
    else if (raf.current) cancelAnimationFrame(raf.current)
    return () => { if (raf.current) cancelAnimationFrame(raf.current) }
  }, [running, draw, onComplete])
  useEffect(() => { if (!running) draw() }, [draw, running])

  const pVT = (p * Vnorm / (T / 300)).toFixed(2)
  return (
    <div className="rounded-2xl border border-cyan-200 bg-white p-4 my-3">
      <h4 className="font-bold text-cyan-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <div className="flex flex-col md:flex-row gap-4">
        <canvas ref={cv} width={W} height={H} className="rounded-lg border border-gray-100 bg-slate-50" />
        <div className="flex-1">
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="p-2 rounded-lg bg-rose-50"><div className="font-black text-rose-700">{p}</div><div className="text-[11px] text-gray-500">Áp suất p</div></div>
            <div className="p-2 rounded-lg bg-cyan-50"><div className="font-black text-cyan-700">{Vnorm.toFixed(2)}</div><div className="text-[11px] text-gray-500">Thể tích V</div></div>
            <div className="p-2 rounded-lg bg-amber-50"><div className="font-black text-amber-700">{T}K</div><div className="text-[11px] text-gray-500">Nhiệt độ T</div></div>
          </div>
          <div className="text-center p-2 rounded-xl bg-emerald-50 mb-3"><span className="font-black text-emerald-700">pV/T = {pVT}</span> <span className="text-xs text-gray-500">(hằng số)</span></div>
          <label className="flex items-center gap-2 text-sm mb-2"><span className="w-24 text-gray-600">Nhiệt độ T</span><input type="range" min={150} max={500} step={10} value={T} onChange={(e) => setT(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{T}K</span></label>
          <label className="flex items-center gap-2 text-sm mb-3"><span className="w-24 text-gray-600">Áp suất p</span><input type="range" min={1} max={6} step={1} value={p} onChange={(e) => setP(Number(e.target.value))} className="flex-1" /><span className="w-12 text-right font-semibold">{p}</span></label>
          <button onClick={() => setRunning((x) => !x)} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 text-white font-bold hover:bg-cyan-700">
            {running ? <><Pause className="w-4 h-4" /> Tạm dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
        </div>
      </div>
    </div>
  )
}
export default GasLawSimulation
